import React, { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { aggregatorsApi, AggregatorApplication, Page } from '../../api/aggregators';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { EmptyState } from '../../components/ui/EmptyState';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import { Spinner } from '../../components/ui/Spinner';
import { date, sectionTitle, td, th } from './shared';

type Filter = 'pending' | 'approved' | 'rejected';

/** The queue: read what they wrote, approve with a rate, or say why not. */
export const ApplicationsPanel: React.FC<{ defaultRate: number | null; onChanged: () => void }> = ({
  defaultRate,
  onChanged,
}) => {
  const toast = useToast();
  const { can } = useAuth();
  const [filter, setFilter] = useState<Filter>('pending');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<Page<AggregatorApplication> | null>(null);
  const [loading, setLoading] = useState(true);

  const [approving, setApproving] = useState<AggregatorApplication | null>(null);
  const [rate, setRate] = useState('');
  const [rejecting, setRejecting] = useState<AggregatorApplication | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setData(await aggregatorsApi.applications({ status: filter, page, limit: 25 }));
    } catch (err) {
      toast.error(err, 'Failed to load applications.');
    } finally {
      setLoading(false);
    }
  }, [filter, page, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const rateValue = rate.trim() === '' ? undefined : Number(rate);
  const rateValid = rateValue === undefined || (rateValue > 0 && rateValue <= 50);

  async function approve() {
    if (!approving || !rateValid) return;
    setBusy(true);
    try {
      await aggregatorsApi.approve(approving.id, rateValue);
      toast.success(`${approving.user.name} is now an aggregator.`);
      setApproving(null);
      setRate('');
      await load();
      onChanged();
    } catch (err) {
      toast.error(err, 'Could not approve this application.');
    } finally {
      setBusy(false);
    }
  }

  async function reject(reason?: string) {
    if (!rejecting || !reason?.trim()) return;
    setBusy(true);
    try {
      await aggregatorsApi.reject(rejecting.id, reason.trim());
      toast.success('Application turned down. They have been told why.');
      setRejecting(null);
      await load();
    } catch (err) {
      toast.error(err, 'Could not turn this application down.');
    } finally {
      setBusy(false);
    }
  }

  const canManage = can('aggregators.manage');

  return (
    <Card padding={20}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <h3 style={sectionTitle}>Applications</h3>
        <div style={{ display: 'flex', gap: 6 }}>
          {(['pending', 'approved', 'rejected'] as Filter[]).map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'primary' : 'ghost'}
              onClick={() => {
                setFilter(f);
                setPage(1);
              }}
            >
              {f === 'pending' ? 'Waiting' : f === 'approved' ? 'Approved' : 'Turned down'}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spinner />
        </div>
      ) : !data?.items.length ? (
        <EmptyState title={filter === 'pending' ? 'No applications waiting.' : 'Nothing here yet.'} />
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 900, borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--neutral-200)' }}>
                  {['Person', 'How they find businesses', 'Where', 'A month', 'Sent', ''].map((h) => (
                    <th key={h} style={th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map((a) => (
                  <tr key={a.id} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: 'var(--neutral-900)' }}>{a.user.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>{a.user.email ?? a.user.phone}</div>
                      {a.user.tier ? (
                        <div style={{ marginTop: 4 }}>
                          <Badge variant="neutral">{a.user.tier.replace('_', ' ')}</Badge>
                        </div>
                      ) : null}
                    </td>
                    <td style={{ ...td, maxWidth: 360, whiteSpace: 'pre-wrap' }}>
                      {a.about}
                      {a.rejectionReason ? (
                        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--danger-600)' }}>
                          Told: {a.rejectionReason}
                        </div>
                      ) : null}
                    </td>
                    <td style={td}>{a.location}</td>
                    <td style={td}>{a.businessesPerMonth}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--neutral-500)' }}>{date(a.createdAt)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {a.status === 'pending' && canManage ? (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Button variant="secondary" onClick={() => setApproving(a)}>
                            Approve
                          </Button>
                          <Button variant="ghost" onClick={() => setRejecting(a)}>
                            Turn down
                          </Button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={data.meta.page}
            totalPages={data.meta.totalPages}
            total={data.meta.total}
            onPageChange={setPage}
          />
        </>
      )}

      <Modal isOpen={!!approving} onClose={() => setApproving(null)} title="Approve as an aggregator" maxWidth={460}>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--neutral-600)' }}>
          {approving?.user.name} will earn this share of what PayIQ makes from every business that signed up with
          their code, from today. You can change it later; what they have earned keeps the rate it was earned at.
        </p>
        <Input
          label="Their share (%)"
          type="number"
          step="0.01"
          min="0.01"
          max="50"
          placeholder={defaultRate === null ? '' : `${defaultRate} (the default)`}
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          error={rateValid ? undefined : 'Above 0% and at most 50%.'}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <Button variant="ghost" onClick={() => setApproving(null)}>
            Cancel
          </Button>
          <Button onClick={() => void approve()} disabled={busy || !rateValid}>
            {busy ? 'Approving…' : 'Approve'}
          </Button>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!rejecting}
        title="Turn down this application"
        message={`${rejecting?.user.name ?? 'They'} will see the reason you give, so write it to them.`}
        confirmLabel="Turn down"
        variant="danger"
        requireReason
        reasonLabel="Reason they will see"
        reasonPlaceholder="For example: tell us more about the businesses you work with."
        loading={busy}
        onConfirm={(reason) => void reject(reason)}
        onCancel={() => setRejecting(null)}
      />
    </Card>
  );
};
